import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ExpensesSubscriptionMembersDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  AddExpensesPlanEmployeesDocument,
  ExpensesSubscriptionsFeatureMembershipDocument,
  GetExpensesPlanEmployeesDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

import type { AddUsersDisabledReason } from 'modules/expenses/lib/RouteContextParams'

interface Props {
  disabledReason?: AddUsersDisabledReason
  onClose: () => void
}
const AddUsersModal: React.FC<Props> = ({ disabledReason, onClose }) => {
  const { company } = useCurrentSession()

  const { data, error } = useQuery(GetExpensesPlanEmployeesDocument, {
    variables: { companyId: company.id },
  })

  const excludedIds = compact(
    data?.features.featureMembership?.members.map((m) => m.access.employee?.id)
  )

  const [newSelectedEmployees, setNewSelectedEmployees] = useState<number[]>([])

  const [addPlanEmployees] = useMutation(AddExpensesPlanEmployeesDocument, {
    refetchQueries: [
      ExpensesSubscriptionMembersDatatableDocument,
      ExpensesSubscriptionsFeatureMembershipDocument,
    ],
  })

  const handleSubmit = async () => {
    if (disabledReason) return

    const result = await addPlanEmployees({
      variables: {
        employeeIds: newSelectedEmployees,
        companyId: company.id,
      },
    })

    if (
      result.errors ||
      !result.data ||
      result.data.featuresMutations.updateMembersFeaturesFeatureMembership.errors.length > 0
    ) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('billing.subscription.plans.expenses.messages.error_adding_users'),
      })
    } else {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('billing.subscription.plans.expenses.messages.users_added_successfully'),
      })
    }
  }

  if (!data && !error) {
    return (
      <Modal
        title={i18n.t('billing.subscription.plans.expenses.actions.add_users')}
        illustration="add-user"
      >
        <Loading expand />
      </Modal>
    )
  }

  return (
    <Modal
      title={i18n.t('billing.subscription.plans.expenses.actions.add_users')}
      onClose={onClose}
      illustration="add-user"
      description={i18n.t('billing.subscription.plans.expenses.add_users_modal.description')}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!!disabledReason}>
            {newSelectedEmployees.length > 1
              ? i18n.t('billing.subscription.plans.expenses.add_users_modal.add_n_users', {
                  count: newSelectedEmployees.length,
                })
              : i18n.t('common.add')}
          </Button>
        </Box>
      }
      disableFocusTrap
    >
      {disabledReason ? (
        <Alert color="error">
          <Text size="300" weight="medium">
            {disabledReason === 'no_ceiling'
              ? i18n.t('billing.subscription.plans.expenses.managed_by_partners.no_ceiling_alert')
              : i18n.t(
                  'billing.subscription.plans.expenses.managed_by_partners.usage_alert_banner'
                )}
          </Text>
        </Alert>
      ) : (
        <Box maxWidth="s400">
          <EmployeeSelectorV2
            multiple
            messages={{
              placeholder: i18n.t(
                'billing.subscription.plans.expenses.add_users_modal.employee_selector_placeholder'
              ),
              searchBoxPlaceholder: i18n.t(
                'billing.subscription.plans.expenses.add_users_modal.employee_selector_search_placeholder'
              ),
              searchEmptyMessage: i18n.t(
                'billing.subscription.plans.expenses.add_users_modal.employee_selector_search_not_found'
              ),
            }}
            options={{ disableSelectAll: true }}
            forcedFilters={{ excludeIds: excludedIds }}
            value={newSelectedEmployees}
            onChange={(state) => {
              if (state.type === 'manual') {
                setNewSelectedEmployees(state.ids)
              }
            }}
            label={i18n.t(
              'billing.subscription.plans.expenses.add_users_modal.employee_selector_label'
            )}
          />
        </Box>
      )}
    </Modal>
  )
}

export default AddUsersModal
